// Workspace-ID privacy: everything sent to analytics goes through
// `hashWorkspaceId` so PostHog never sees raw tenant identifiers.
//
// Exception: the three reserved system workspaces ('demo', 'govard-public',
// 'govard-public-guest') are passed through unhashed — they are already public
// by design and hashing them would make cross-service correlation impossible.
//
// Implementation notes:
//   - The hash must run identically in Node and in the browser so that any
//     correlation in Phase 2 (once we ship `posthog-js`) matches server-side
//     enrichment. The original implementation used `node:crypto`'s `createHash`
//     which Vite cannot bundle for the browser (it gets externalised and
//     throws at load time).
//   - Phase 1 keeps a dependency-free 64-bit FNV-1a computed with native
//     `BigInt`. It is deterministic, salt-sensitive, isomorphic, and yields
//     the same 16-hex-char fingerprint shape the rest of the codebase relies
//     on (`^[0-9a-f]{16}$`). It is NOT cryptographically secure — that is fine
//     for our bucketing use case (we just need non-reversibility for opaque
//     workspace slugs, not resistance to adversarial collision).
//   - TODO [PHASE-2]: when real PostHog transports ship, revisit whether we
//     need SHA-256 parity with server-side tooling. If so, swap this for an
//     async `crypto.subtle.digest` on the browser + `node:crypto` on Node,
//     behind the same (then async) API.

const SPECIAL_WORKSPACES: readonly string[] = [
  'demo',
  'govard-public',
  'govard-public-guest',
] as const;

const DEFAULT_SALT = 'dev-salt';

// FNV-1a 64-bit constants — see http://www.isthe.com/chongo/tech/comp/fnv/.
const FNV_OFFSET_BASIS = 0xcbf29ce484222325n;
const FNV_PRIME = 0x100000001b3n;
const MASK_64 = 0xffffffffffffffffn;

function fnv1a64Hex(input: string): string {
  let hash = FNV_OFFSET_BASIS;
  for (let i = 0; i < input.length; i++) {
    // Operate on UTF-16 code units — matches Node's `Buffer.from(str)` byte
    // sequence poorly for astral chars, which we deliberately accept because
    // workspace slugs are ASCII by construction.
    hash = (hash ^ BigInt(input.charCodeAt(i))) & MASK_64;
    hash = (hash * FNV_PRIME) & MASK_64;
  }
  return hash.toString(16).padStart(16, '0');
}

function readEnvSalt(): string | undefined {
  // Guarded access so browser bundles (where `process` may be undefined)
  // don't throw. Vite replaces `process.env.*` at build time for known keys
  // only; this check survives regardless.
  const proc =
    typeof globalThis !== 'undefined'
      ? (globalThis as { process?: { env?: Record<string, string | undefined> } }).process
      : undefined;
  return proc?.env?.['ANALYTICS_SALT'];
}

/**
 * fnv1a64(workspaceId + salt) rendered as 16 hex chars. Special workspaces
 * pass through. Sufficient entropy for bucketing without leaking tenant slugs.
 */
export function hashWorkspaceId(id: string, salt?: string): string {
  if (SPECIAL_WORKSPACES.includes(id)) return id;
  const effectiveSalt = salt ?? readEnvSalt() ?? DEFAULT_SALT;
  return fnv1a64Hex(`${id}${effectiveSalt}`);
}

/**
 * Exposed for tests and apps that need to double-check whether a slug is
 * considered public.
 */
export function isSpecialWorkspaceForAnalytics(id: string): boolean {
  return SPECIAL_WORKSPACES.includes(id);
}
